/**
 * Time-based grouping utilities for history threads
 */

/**
 * Categorize a thread by its date into time groups
 * @param {Object} thread - Thread object
 * @param {Date} today - Today's date
 * @param {Date} yesterday - Yesterday's date
 * @returns {string|null} Group label or null if invalid
 */
export function groupThreadByDate(thread, today, yesterday) {
  const dateField = thread?.updated_at || thread?.lastMessageAt;
  if (!dateField) return null;

  try {
    const threadDate = new Date(dateField);
    if (isNaN(threadDate.getTime())) return null;

    // Handle shared thread metadata
    const isSharedThread =
      thread.metadata?.is_shared_thread || thread.metadata?.shared_in_workspace;

    if (isSharedThread && !thread.metadata?.is_shared_thread) {
      thread.metadata = {
        ...thread.metadata,
        is_shared_thread: true,
      };
    }

    if (threadDate.toDateString() === today.toDateString()) return "Today";
    if (threadDate.toDateString() === yesterday.toDateString())
      return "Yesterday";
    if (threadDate >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000))
      return "Previous 7 days";
    if (threadDate >= new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000))
      return "Previous 30 days";
    return "Older";
  } catch (err) {
    console.error("Error parsing thread date:", err);
    return null;
  }
}

/**
 * Filter threads for history display
 * @param {Array} threads - Array of threads
 * @param {Array} workspaces - Array of workspaces
 * @returns {Array} Filtered threads
 */
export function filterHistoryThreads(threads, workspaces) {
  // Defensive null checks - treat null/undefined as empty arrays
  const safeThreads = Array.isArray(threads) ? threads : [];
  const safeWorkspaces = Array.isArray(workspaces) ? workspaces : [];

  // Early return if no threads
  if (safeThreads.length === 0) {
    return [];
  }

  // Deduplicate threads
  const threadIds = new Set();
  const uniqueThreads = safeThreads.filter((thread) => {
    const threadIdentifier = thread?.id || thread;
    if (threadIds.has(threadIdentifier)) {
      return false;
    }
    threadIds.add(threadIdentifier);
    return true;
  });

  // Filter threads
  return uniqueThreads.filter((thread) => {
    // Skip invalid thread objects
    if (!thread || typeof thread !== "object") {
      return false;
    }

    // Exclude "New Chat" threads
    if (thread?.title === "New Chat") {
      return false;
    }

    // Exclude threads that are in workspaces
    const isInWorkspaceThreads = safeWorkspaces.some(
      (workspace) =>
        workspace?.workspace_threads &&
        Array.isArray(workspace.workspace_threads) &&
        workspace.workspace_threads.includes(thread.id)
    );
    if (isInWorkspaceThreads) {
      return false;
    }

    // Handle team threads - only show if moved to history
    const isTeamThread = thread.is_team === true;
    if (isTeamThread) {
      const hasBeenMovedToHistory = thread.metadata?.moved_to_history_at;
      return hasBeenMovedToHistory;
    }

    return true;
  });
}

/**
 * Group threads by time periods
 * @param {Array} threads - Array of threads
 * @param {Array} workspaces - Array of workspaces
 * @returns {Array} Array of grouped threads with labels
 */
export function groupHistoryThreads(threads, workspaces) {
  try {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const filteredThreads = filterHistoryThreads(threads, workspaces);

    const groupedThreads = filteredThreads.reduce((acc, thread) => {
      const group = groupThreadByDate(thread, today, yesterday);
      if (group) {
        acc[group] = acc[group] || [];
        acc[group].push(thread);
      }
      return acc;
    }, {});

    // Sort threads within each group by date (newest first)
    const sortThreadsByDate = (threads) => {
      return threads.sort((a, b) => {
        const dateA = new Date(a?.updated_at || a?.lastMessageAt || 0);
        const dateB = new Date(b?.updated_at || b?.lastMessageAt || 0);
        return dateB - dateA; // Most recent first
      });
    };

    return [
      {
        label: "Today",
        threads: sortThreadsByDate(groupedThreads["Today"] || []),
      },
      {
        label: "Yesterday",
        threads: sortThreadsByDate(groupedThreads["Yesterday"] || []),
      },
      {
        label: "Previous 7 days",
        threads: sortThreadsByDate(groupedThreads["Previous 7 days"] || []),
      },
      {
        label: "Previous 30 days",
        threads: sortThreadsByDate(groupedThreads["Previous 30 days"] || []),
      },
      {
        label: "Older",
        threads: sortThreadsByDate(groupedThreads["Older"] || []),
      },
    ];
  } catch (err) {
    console.error("Error grouping threads:", err);
    return [
      { label: "Today", threads: [] },
      { label: "Yesterday", threads: [] },
      { label: "Previous 7 days", threads: [] },
      { label: "Previous 30 days", threads: [] },
      { label: "Older", threads: [] },
    ];
  }
}

/**
 * Initial history groups structure
 */
export const initialHistoryGroups = [
  { label: "Today", threads: [] },
  { label: "Yesterday", threads: [] },
  { label: "Previous 7 days", threads: [] },
  { label: "Previous 30 days", threads: [] },
  { label: "Older", threads: [] },
];
